import type { HoverIdleSaveData } from "./hoverIdleSaveData";
import {
  INDEXED_DB_LATEST_SAVE_KEY,
  readFirstIndexedDbSaveRecord,
  transformIndexedDbSaveRecord,
  writeIndexedDbSaveRecord,
  type IndexedDbSaveRecord,
} from "./indexedDbSaveStore";
import {
  type OfflineRewardCommitResult,
  type OfflineRewardRepository,
  type SaveRepository,
} from "./saveRepository";
import { decodeHoverIdleSaveData } from "./hoverIdleSaveCodec";
import { canonicalDigest } from "@/features/hoverIdle/offline/offlineRewardDigest";
import { validateTimOfflineReward } from "@/features/hoverIdle/offline/validateTimOfflineReward";
import type { OfflineRewardCommitFailure } from "@/features/hoverIdle/offline/offlineRewardTypes";

const LEGACY_LATEST_SAVE_KEY = INDEXED_DB_LATEST_SAVE_KEY;

function ownerKey(ownerId: string): string {
  return `latest:${ownerId}`;
}

export type LocalSaveRepository = SaveRepository & OfflineRewardRepository;

export function createLocalSaveRepository(): LocalSaveRepository {
  return {
    async load(ownerId) {
      const record = await readFirstIndexedDbSaveRecord([
        ownerKey(ownerId),
        LEGACY_LATEST_SAVE_KEY,
      ]);
      if (record === undefined) {
        return null;
      }
      return extractData(record);
    },

    async save(ownerId, saveData) {
      await writeIndexedDbSaveRecord({
        id: ownerKey(ownerId),
        savedAt: saveData.timestamps.savedAt,
        data: saveData,
      } satisfies IndexedDbSaveRecord<HoverIdleSaveData>);
    },

    async commitOfflineReward(ownerId, request) {
      const validation = validateTimOfflineReward(
        request.input,
        request.result,
      );
      if (!validation.ok) {
        return {
          ok: false,
          reason: {
            code: "invalid-result",
            message: validation.errors.map((error) => error.message).join("; "),
          },
        };
      }
      const committedSave = request.result.finalSave;
      const transformed = await transformIndexedDbSaveRecord(
        [ownerKey(ownerId), LEGACY_LATEST_SAVE_KEY],
        (current) => {
          const precondition = validateCommitPrecondition(current, request);
          if (!precondition.ok) return precondition;
          return {
            ok: true,
            record: {
              id: ownerKey(ownerId),
              savedAt: committedSave.timestamps.savedAt,
              data: committedSave,
            } satisfies IndexedDbSaveRecord<HoverIdleSaveData>,
          };
        },
      );
      if (!transformed.ok) {
        return transformed as OfflineRewardCommitResult;
      }
      return { ok: true, saveData: committedSave };
    },
  };
}

function validateCommitPrecondition(
  currentRecord: unknown | undefined,
  request: Parameters<OfflineRewardRepository["commitOfflineReward"]>[1],
):
  | { readonly ok: true }
  | {
      readonly ok: false;
      readonly reason: OfflineRewardCommitFailure;
    } {
  if (currentRecord === undefined) {
    return failure(
      "stale-input-digest",
      "offline reward start save no longer exists",
    );
  }
  const decoded = decodeHoverIdleSaveData(extractData(currentRecord));
  if (!decoded.ok) {
    return failure(
      "stale-input-digest",
      `current save is invalid: ${decoded.reason}`,
    );
  }
  const current = decoded.data;
  if (
    current.offlineReward.appliedRewardIds.includes(request.result.rewardId)
  ) {
    return failure(
      "duplicate-reward",
      "offline reward ID was already committed",
    );
  }
  if (current.offlineReward.revision !== request.input.revision) {
    return failure("stale-revision", "save revision changed during reward");
  }
  if (current.offlineReward.baselineAt !== request.input.baseline.previousAt) {
    return failure(
      "stale-baseline",
      "offline baseline changed during reward commit",
    );
  }
  if (canonicalDigest(current) !== request.input.inputDigest) {
    return failure(
      "stale-input-digest",
      "save snapshot changed during reward commit",
    );
  }
  return { ok: true };
}

function failure(
  code:
    | "stale-revision"
    | "stale-baseline"
    | "stale-input-digest"
    | "duplicate-reward",
  message: string,
) {
  return { ok: false as const, reason: { code, message } };
}

function extractData(record: unknown): unknown {
  if (typeof record !== "object" || record === null || Array.isArray(record)) {
    return record;
  }
  const candidate = record as { data?: unknown };
  return candidate.data !== undefined ? candidate.data : record;
}
